import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Confidence, ItemType, TriageStatus } from "@/hooks/use-baseline";

export type DecisionReason = "change-order" | "roadmap" | "reject";
export type Classification = "L1" | "L2" | "L3" | "AC";
export type DecisionCoKind = "swap" | "new";

export interface TriageDecision {
  id: string;
  itemId: string;
  status: TriageStatus;
  reason: DecisionReason | null;
  classification: Classification | null;
  coKind: DecisionCoKind | null;
  decidedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface DecisionPatchBody {
  status?: TriageStatus;
  reason?: DecisionReason | null;
  classification?: Classification | null;
  coKind?: DecisionCoKind | null;
}

export interface TriageItemRow {
  id: string;
  solutionId: string;
  externalKey: string | null;
  sourceId: string;
  speakerId: string | null;
  type: ItemType;
  confidence: Confidence;
  extracted: string;
  quote: string;
  quoteContext: string | null;
  sourceDate: string;
  mapStoryId: string | null;
  overlapPct: number;
  sizeEstimate: string | null;
  suggestedStatus: TriageStatus;
  effectiveStatus: TriageStatus;
  decision: TriageDecision | null;
  source: {
    id: string;
    name: string;
    type: string;
    sourceDate: string;
    externalKey: string | null;
  };
  speaker: {
    id: string;
    name: string;
    role: string;
    initials: string;
    org: "client" | "nymbl";
  } | null;
  mapDeliverable: { id: string; code: string; title: string } | null;
  mapEpic: { id: string; code: string; title: string } | null;
  mapStory: { id: string; code: string; label: string | null; text: string } | null;
  notes: Array<{
    id: string;
    text: string;
    authorName: string | null;
    createdAt: string;
  }>;
  reviewerStatuses: Array<{
    id: string;
    pass: string | null;
    status: string;
    note: string | null;
  }>;
}

export interface TriageNote {
  id: string;
  text: string;
  authorName: string | null;
  createdAt: string;
}

export interface TriageItemDetail extends TriageItemRow {
  relatedItemIds: string[];
  linkedQuestionIds: string[];
}

export interface TriageItemListResponse {
  data: TriageItemRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface TriageFilters {
  status?: TriageStatus;
  type?: ItemType;
  speakerId?: string;
  search?: string;
}

export const triageKeys = {
  all: ["triage"] as const,
  list: (solutionId: string, filters: TriageFilters) =>
    ["triage", "list", solutionId, filters] as const,
  detail: (itemId: string) => ["triage", "detail", itemId] as const,
  notes: (itemId: string) => ["triage", "notes", itemId] as const,
} as const;

export function useTriageItems(solutionId: string, filters: TriageFilters) {
  return useQuery({
    queryKey: triageKeys.list(solutionId, filters),
    queryFn: async (): Promise<TriageItemListResponse> => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.type) params.set("type", filters.type);
      if (filters.speakerId) params.set("speakerId", filters.speakerId);
      if (filters.search) params.set("search", filters.search);
      const res = await fetch(
        `/api/solutions/${solutionId}/triage-items${params.size ? `?${params}` : ""}`,
      );
      if (!res.ok) throw new Error("Failed to fetch triage items");
      return res.json();
    },
    enabled: Boolean(solutionId),
  });
}

export function useTriageItem(itemId: string | null) {
  return useQuery({
    queryKey: triageKeys.detail(itemId ?? ""),
    queryFn: async (): Promise<TriageItemDetail> => {
      const res = await fetch(`/api/triage-items/${itemId}`);
      if (!res.ok) throw new Error("Failed to fetch triage item");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(itemId),
  });
}

function invalidateTriage(qc: QueryClient, itemId: string) {
  qc.invalidateQueries({ queryKey: triageKeys.all });
  qc.invalidateQueries({ queryKey: triageKeys.detail(itemId) });
}

export function usePatchDecision(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: DecisionPatchBody): Promise<TriageDecision> => {
      const res = await fetch(`/api/triage-items/${itemId}/decision`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        throw new Error(body?.error?.message ?? "Failed to update decision");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => invalidateTriage(qc, itemId),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useClearDecision(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/triage-items/${itemId}/decision`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to clear decision");
    },
    onSuccess: () => invalidateTriage(qc, itemId),
    onError: () => toast.error("Failed to clear decision"),
  });
}

export function useAddNote(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (text: string): Promise<TriageNote> => {
      const res = await fetch(`/api/triage-items/${itemId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("Failed to add note");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: triageKeys.detail(itemId) }),
    onError: () => toast.error("Failed to add note"),
  });
}

export function useDeleteNote(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const res = await fetch(`/api/notes/${noteId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete note");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: triageKeys.detail(itemId) }),
    onError: () => toast.error("Failed to delete note"),
  });
}
